/**
 * Route permissions configuration
 *
 * Maps route paths to required permissions for RBAC enforcement.
 * Each route is mapped to an array of PermissionType values from @/types/auth.
 *
 * Only routes reachable from the sidebar or linked from sidebar-accessible
 * pages are included. Dead/unreachable routes have been removed.
 */

import { Permission, type PermissionType } from '@/types/auth'

/**
 * Route permissions map
 * Maps route paths (with params as :id) to the required permissions
 */
export const ROUTE_PERMISSIONS: Record<string, PermissionType[]> = {
  // Dashboard — accessible to all authenticated users
  '/': [],

  // OR-409 Command Centre — executive resilience overview. Gated like an
  // overview (Performance Hub Overview.ReadOnly); SIDEBAR_PERMISSIONS falls
  // back to this entry, so the sidebar item and the route stay in sync.
  '/command-centre': [Permission.DASHBOARD_VIEW],

  // Performance Hub (OR-312 rehomed pages) — these are sidebar destinations
  // and MUST be gated here, otherwise ProtectedRoute lets the page load and
  // the content errors with "permission not allowed" (Indi UAT 4-Jun-2026).
  '/performance-hub/ibs': [Permission.IBS_VIEW],
  '/performance-hub/operational-components': [
    Permission.OC_MICROSERVICES_VIEW,
    Permission.OC_SYSTEMS_VIEW,
    Permission.OC_INFRASTRUCTURE_VIEW,
    Permission.OC_LOCATIONS_VIEW,
    Permission.OC_TEAMS_VIEW,
    Permission.OC_SUPPLIERS_VIEW,
    Permission.OC_RELATIONSHIPS_VIEW,
    Permission.OC_CDE_VIEW,
  ],
  '/performance-hub/bulk-performance-upload': [
    Permission.ADMIN_PERF_VIEW,
    Permission.ADMIN_PERF_EDIT,
  ],

  // Configure Hub (OR-312) — sidebar destinations
  '/configure-hub/ibs': [Permission.IBS_UPDATE],
  '/configure-hub/components': [Permission.COMPONENTS_EDIT],

  // Client Admin (user & role management) — sidebar destinations
  '/client-admin/users': [Permission.USER_VIEW],
  '/client-admin/roles': [Permission.SYSTEM_VIEW_ALL],

  // IBS routes
  '/ibs': [Permission.IBS_VIEW],
  '/ibs/tree': [Permission.IBS_VIEW],
  '/ibs/create': [Permission.IBS_CREATE],
  '/ibs/list': [Permission.IBS_VIEW],
  '/ibs/:id': [Permission.IBS_VIEW],
  '/ibs/:id/edit': [Permission.IBS_UPDATE],
  '/ibs/:id/continue': [Permission.IBS_UPDATE],
  // OR-323: /ibs/:id/flows page retired; route still resolves via a
  // redirect to /ibs/:id, which is permission-gated separately.
  // OR-312: flow create/edit gated by IBS Flows edit specifically, not IBS edit.
  '/ibs/:id/flows/create': [Permission.IBS_FLOWS_EDIT],
  '/ibs/:id/flows/:flowId': [Permission.IBS_VIEW],
  '/ibs/:id/flows/:flowId/edit': [Permission.IBS_FLOWS_EDIT],
  '/ibs/:id/microservices': [Permission.OC_MICROSERVICES_VIEW],
  '/ibs/:id/microservices-test': [Permission.OC_MICROSERVICES_VIEW],
  '/ibs/:id/microservices/:microserviceId': [Permission.OC_MICROSERVICES_VIEW],

  // Microservices routes
  '/microservices': [Permission.OC_MICROSERVICES_VIEW],
  '/microservices/registry': [Permission.OC_MICROSERVICES_VIEW],
  '/microservices/new': [Permission.OC_MICROSERVICES_EDIT],
  '/microservices/:id': [Permission.OC_MICROSERVICES_VIEW],
  '/microservices/:id/edit': [Permission.OC_MICROSERVICES_EDIT],
  '/microservices/:id/performance': [Permission.OC_MICROSERVICES_VIEW],

  // Systems routes
  '/systems': [Permission.OC_SYSTEMS_VIEW],
  '/systems-registry': [Permission.OC_SYSTEMS_VIEW],
  '/systems-registry/new': [Permission.OC_SYSTEMS_EDIT],
  '/systems-registry/:id': [Permission.OC_SYSTEMS_VIEW],
  '/systems-registry/:id/edit': [Permission.OC_SYSTEMS_EDIT],
  '/systems/:id/performance': [Permission.OC_SYSTEMS_VIEW],

  // Infrastructure routes
  '/infrastructure': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/infrastructure-registry': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/infrastructure-registry/new': [Permission.OC_INFRASTRUCTURE_EDIT],
  '/infrastructure-registry/:id': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/infrastructure-registry/:id/edit': [Permission.OC_INFRASTRUCTURE_EDIT],
  '/infrastructure/:id/performance': [Permission.OC_INFRASTRUCTURE_VIEW],

  // Locations routes
  '/locations-registry': [Permission.OC_LOCATIONS_VIEW],

  // Teams routes
  '/teams-registry': [Permission.OC_TEAMS_VIEW],
  '/teams-registry/:id': [Permission.OC_TEAMS_VIEW],

  // Suppliers routes
  '/suppliers': [Permission.OC_SUPPLIERS_VIEW],
  '/supplier-alternate': [Permission.OC_SUPPLIERS_VIEW],

  // Relationships routes
  '/relationships': [Permission.OC_RELATIONSHIPS_VIEW],


  // Organization and Resources routes
  '/organization/resource-map': [Permission.OC_SYSTEMS_VIEW],
  '/resources/linkage': [Permission.OC_SYSTEMS_VIEW],

  // Scenarios routes
  '/scenarios/new': [Permission.SCENARIO_CREATE],
  // OR-312 gap-filler: Wizard has its own gate, distinct from Scenarios.Edit.
  '/scenarios/wizard': [Permission.SCENARIO_WIZARD_RUN],
  // OR-364 resume-draft variant must carry the same gate; without this entry
  // /scenarios/wizard/:scenarioId falls through to authenticated-only and
  // exposes the wizard's cross-domain create surfaces to any signed-in user.
  '/scenarios/wizard/:scenarioId': [Permission.SCENARIO_WIZARD_RUN],
  '/scenarios/analyse': [Permission.SCENARIO_CREATE],
  '/scenarios/ibs-selection': [Permission.SCENARIO_VIEW],
  '/scenarios/library': [Permission.SCENARIO_VIEW],
  '/scenarios/enhanced': [Permission.SCENARIO_VIEW],
  '/scenario-definition': [Permission.SCENARIO_CREATE],

  // Testing routes
  '/testing': [Permission.SCENARIO_VIEW],

  // Risks routes
  '/risks': [Permission.RISK_VIEW],

  // Critical Data Elements routes
  '/critical-data': [Permission.OC_CDE_VIEW],
  '/critical-data/new': [Permission.OC_CDE_EDIT],
  '/critical-data/:id/edit': [Permission.OC_CDE_EDIT],

  // E2E Flows routes
  '/e2e-flows': [Permission.IBS_VIEW],

  // Incidents routes
  '/incidents': [Permission.INCIDENT_VIEW],

  // Knowledge Hub (Vault) routes
  '/knowledge-hub': [Permission.VAULT_READONLY],
  '/knowledge-hub/documents/:id': [Permission.VAULT_READONLY],
  '/knowledge-hub/documents/:id/diff': [Permission.VAULT_READONLY],

  // Admin routes
  '/admin': [Permission.ADMIN_VIEW_AUDIT],
  '/admin/microservices': [Permission.OC_MICROSERVICES_VIEW],
  '/admin/microservices/:id': [Permission.OC_MICROSERVICES_VIEW],
  '/admin/systems': [Permission.OC_SYSTEMS_VIEW],
  '/admin/systems/:id': [Permission.OC_SYSTEMS_VIEW],
  '/admin/system-categories': [Permission.OC_SYSTEMS_VIEW],
  '/admin/infrastructure-categories': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/admin/infrastructure': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/admin/infrastructure/:id': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/admin/locations': [Permission.OC_LOCATIONS_VIEW],
  '/admin/teams': [Permission.OC_TEAMS_VIEW],
  '/admin/teams/:id': [Permission.OC_TEAMS_VIEW],
  '/admin/relationships': [Permission.OC_RELATIONSHIPS_VIEW],
  '/admin/suppliers': [Permission.OC_SUPPLIERS_VIEW],
  '/admin/suppliers/:id': [Permission.OC_SUPPLIERS_VIEW],
  '/admin/bulk-upload': [Permission.OC_SYSTEMS_EDIT],
  '/admin/systems/:systemId/performance': [Permission.OC_SYSTEMS_VIEW],
  '/admin/microservices/:microserviceId/performance': [Permission.OC_MICROSERVICES_VIEW],
  '/admin/infrastructure/:infrastructureId/performance': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/admin/bulk-performance-upload': [Permission.OC_SYSTEMS_EDIT],
  // AI configuration is a Super User settings surface.
  '/admin/ai-settings': [Permission.ADMIN_SETTINGS_VIEW, Permission.ADMIN_SETTINGS_EDIT],

  // Visualisation routes
  '/visualization': [Permission.IBS_VIEW],
  '/visualization/layered': [Permission.IBS_VIEW],
  '/visualization/ibs/:ibsId': [Permission.IBS_VIEW],

  // Impact routes
  '/impact': [Permission.IBS_VIEW],

  // CMORG Gap Analysis - Testing routes
  '/test-outcomes': [Permission.SCENARIO_VIEW],
  '/post-test-reviews': [Permission.SCENARIO_VIEW],
  '/testing-calendar': [Permission.SCENARIO_VIEW],
  '/sophistication-mapping': [Permission.SCENARIO_VIEW],
  '/response-assessments': [Permission.SCENARIO_VIEW],

  // CMORG Gap Analysis - Scenario routes
  '/scenarios/causation-matrix': [Permission.SCENARIO_VIEW],
  '/scenarios/detail/:id': [Permission.SCENARIO_VIEW],

  // CMORG Library (OR-312 gap-filler: separate gate from Scenarios.ReadOnly)
  '/scenarios/cmorg-library': [Permission.CMORG_LIBRARY_VIEW],

  // Public routes
  '/auth/login': [],
  '/callback': [],
  '/help': [],
  '/connection-test': [],
}

/**
 * Sidebar permissions map
 * Maps sidebar href values to required permissions for visibility control
 */
export const SIDEBAR_PERMISSIONS: Record<string, PermissionType[]> = {
  // Home Dashboard
  '/': [Permission.DASHBOARD_VIEW],

  // Operational Components
  '/ibs': [Permission.IBS_VIEW],
  '/e2e-flows': [Permission.IBS_VIEW],
  '/microservices': [Permission.OC_MICROSERVICES_VIEW],
  '/systems': [Permission.OC_SYSTEMS_VIEW],
  '/infrastructure': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/locations-registry': [Permission.OC_LOCATIONS_VIEW],
  '/teams-registry': [Permission.OC_TEAMS_VIEW],
  '/suppliers': [Permission.OC_SUPPLIERS_VIEW],
  '/supplier-alternate': [Permission.OC_SUPPLIERS_VIEW],
  '/relationships': [Permission.OC_RELATIONSHIPS_VIEW],
  '/risks': [Permission.RISK_VIEW],
  '/incidents': [Permission.INCIDENT_VIEW],

  // Scenario Testing
  '/scenarios/library': [Permission.SCENARIO_VIEW],
  '/scenarios/enhanced': [Permission.SCENARIO_VIEW],
  '/scenarios/ibs-selection': [Permission.SCENARIO_VIEW],
  // OR-312 gap-filler: Scenario Wizard has its own gate now, distinct from
  // Scenarios.Edit. Wizard run is allowed for Operator + Owner + Super User.
  '/scenarios/wizard': [Permission.SCENARIO_WIZARD_RUN],
  '/scenario-definition': [Permission.SCENARIO_CREATE],
  '/testing': [Permission.SCENARIO_VIEW],

  // Configure Hub (OR-312)
  '/configure-hub/ibs': [Permission.IBS_UPDATE],
  '/configure-hub/components': [Permission.COMPONENTS_EDIT],

  // Operational Components (Critical Data Elements)
  '/critical-data': [Permission.OC_CDE_VIEW],

  // Knowledge Hub (Vault)
  '/knowledge-hub': [Permission.VAULT_READONLY],

  // Administration
  '/uat': [Permission.ADMIN_VIEW_AUDIT],
  '/admin': [Permission.ADMIN_VIEW_AUDIT],
  '/admin/microservices': [Permission.OC_MICROSERVICES_VIEW],
  '/admin/systems': [Permission.OC_SYSTEMS_VIEW],
  '/admin/system-categories': [Permission.OC_SYSTEMS_VIEW],
  '/admin/infrastructure': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/admin/infrastructure-categories': [Permission.OC_INFRASTRUCTURE_VIEW],
  '/admin/locations': [Permission.OC_LOCATIONS_VIEW],
  '/admin/teams': [Permission.OC_TEAMS_VIEW],
  '/admin/relationships': [Permission.OC_RELATIONSHIPS_VIEW],
  '/admin/suppliers': [Permission.OC_SUPPLIERS_VIEW],
  '/admin/bulk-upload': [Permission.OC_SYSTEMS_EDIT],
  '/admin/bulk-performance-upload': [Permission.OC_SYSTEMS_EDIT],

  // User Admin routes
  '/client-admin/users': [Permission.USER_VIEW],
  '/client-admin/roles': [Permission.SYSTEM_VIEW_ALL],

  // CMORG Gap Analysis - Testing
  '/test-outcomes': [Permission.SCENARIO_VIEW],
  '/post-test-reviews': [Permission.SCENARIO_VIEW],
  '/testing-calendar': [Permission.SCENARIO_VIEW],
  '/sophistication-mapping': [Permission.SCENARIO_VIEW],
  '/response-assessments': [Permission.SCENARIO_VIEW],

  // CMORG Gap Analysis - Scenarios
  '/scenarios/causation-matrix': [Permission.SCENARIO_VIEW],

  // CMORG Library (OR-312 gap-filler: separate gate from Scenarios.ReadOnly)
  '/scenarios/cmorg-library': [Permission.CMORG_LIBRARY_VIEW],
}

/**
 * Get the required permissions for a given route path
 * Handles parameterised routes (e.g., /ibs/:id) by matching patterns
 *
 * @param path - The route path to check (e.g., '/ibs/123' or '/ibs/:id')
 * @returns Array of required permissions, or empty array if no permissions required
 */
export function getRoutePermissions(path: string): PermissionType[] {
  // Direct match first
  if (ROUTE_PERMISSIONS[path]) {
    return ROUTE_PERMISSIONS[path]
  }

  // Try to match parameterised routes
  // Split the path into segments
  const pathSegments = path.split('/').filter(Boolean)

  // Find a matching pattern in ROUTE_PERMISSIONS
  for (const [routePattern, permissions] of Object.entries(ROUTE_PERMISSIONS)) {
    const patternSegments = routePattern.split('/').filter(Boolean)

    // Skip if segment counts don't match
    if (pathSegments.length !== patternSegments.length) {
      continue
    }

    // Check if all segments match (considering :param as wildcards)
    const matches = patternSegments.every((patternSegment, index) => {
      return patternSegment.startsWith(':') || patternSegment === pathSegments[index]
    })

    if (matches) {
      return permissions
    }
  }

  // No permissions required if no match found (defaults to authenticated only)
  return []
}

/**
 * Get the required permissions for a sidebar item
 *
 * @param href - The sidebar href to check
 * @returns Array of required permissions, or empty array if no permissions required
 */
export function getSidebarPermissions(href: string): PermissionType[] {
  // Single source of truth (Consumer Duty strategy): a sidebar item is gated by
  // its destination route's permissions unless an explicit override is set.
  // This guarantees "if you can't open the page, you don't see it in the nav"
  // and stops SIDEBAR_PERMISSIONS drifting from ROUTE_PERMISSIONS.
  return SIDEBAR_PERMISSIONS[href] ?? getRoutePermissions(href)
}

/**
 * Check if a user has permission to access a route
 *
 * @param path - The route path to check
 * @param userPermissions - Array of permissions the user has
 * @returns true if user has at least one of the required permissions (or no permissions required)
 */
export function canAccessRoute(path: string, userPermissions: string[]): boolean {
  const requiredPermissions = getRoutePermissions(path)

  // If no permissions required, allow access
  if (requiredPermissions.length === 0) {
    return true
  }

  // Check if user has at least one of the required permissions
  return requiredPermissions.some(permission => userPermissions.includes(permission))
}

/**
 * Check if a user has permission to view a sidebar item
 *
 * @param href - The sidebar href to check
 * @param userPermissions - Array of permissions the user has
 * @returns true if user has at least one of the required permissions (or no permissions required)
 */
export function canViewSidebarItem(href: string, userPermissions: string[]): boolean {
  const requiredPermissions = getSidebarPermissions(href)

  // If no permissions required, allow access
  if (requiredPermissions.length === 0) {
    return true
  }

  // Check if user has at least one of the required permissions
  return requiredPermissions.some(permission => userPermissions.includes(permission))
}
